import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import Welcome from './components/Welcome';
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';
import ScoutLogin from './components/auth/ScoutLogin';
import ScoutSignup from './components/auth/ScoutSignup';
import PlayerDashboard from './components/PlayerDashboard';
import AdminDashboard from './components/AdminDashboard';
import MasterDashboard from './components/MasterDashboard';
import ScoutDashboard from './components/scout/ScoutDashboard';
import ScoutBowlerDetail from './components/scout/ScoutBowlerDetail';
import CreateLeague from './components/admin/CreateLeague';
import ManageLeagues from './components/admin/ManageLeagues';
import ManagingLeague from './components/admin/ManagingLeague';
import BowlersManagement from './components/admin/BowlersManagement';
import BowlerDetail from './components/admin/BowlerDetail';
import CheckoutComponent from './components/admin/CheckoutComponent';
import BrowseLeagues from './components/player/BrowseLeagues';
import LeagueDetail from './components/player/LeagueDetail';
import MyTeams from './components/player/MyTeams';
import ProtectedRoute from './components/ProtectedRoute';
import ResetPassword from './components/auth/ResetPassword';
import DoublesList from './components/admin/DoublesList';
import Analytics from './components/admin/Analytics';
import PlayerAnalytics from './components/player/PlayerAnalytics';
import BowlerLeagueView from './components/player/BowlerLeagueView';
import BowlerCalendar from './components/player/BowlerCalendar';
import SubscriptionSuccess from './components/player/SubscriptionSuccess';
import ManageStaff from './components/admin/ManageStaff';
import AdminBillingCheckout from './components/admin/AdminBillingCheckout';
import AdminBillingSuccess from './components/admin/AdminBillingSuccess';
import BookieLogin from './components/auth/BookieLogin';
import BookieSignup from './components/auth/BookieSignup';
import SideActionDashboard from './components/SideActionDashboard';
import BookieSubscriptionSuccess from './components/bookie/BookieSubscriptionSuccess';
import CreateExpressLeague from './components/bookie/CreateExpressLeague';
import CreateBowlerAccount from './components/bookie/CreateBowlerAccount';
import BookieLeagueList from './components/bookie/BookieLeagueList';
import BookieLeagueManager from './components/bookie/BookieLeagueManager';
import CreateBowlerGroups from './components/bookie/CreateBowlerGroups';
import QuickEntriesPage from './components/bookie/QuickEntriesPage';
import QuickBracketEntriesPage from './components/bookie/QuickBracketEntriesPage';
import AdminQuickEntriesPage from './components/admin/AdminQuickEntriesPage';
import AdminQuickBracketEntriesPage from './components/admin/AdminQuickBracketEntriesPage';
import BowlerSideActionView from './components/player/BowlerSideActionView';
import PublicSideActionList from './components/PublicSideActionList';
import PublicSideActionView from './components/PublicSideActionView';

// Admin Dashboard Router Component
const AdminDashboardRouter: React.FC = () => {
  const { user } = require('./contexts/AuthContext').useAuth();
  
  // Check if user is super admin
  if (user?.isSuperAdmin) {
    return <MasterDashboard />;
  }
  
  return <AdminDashboard />;
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <Routes>
            {/* Welcome/Landing Page */}
            <Route path="/" element={<Welcome />} />

            {/* Authentication Routes */}
            <Route path="/login/player" element={<Login userType="player" />} />
            <Route path="/login/admin" element={<Login userType="admin" />} />
            <Route path="/login/scout" element={<ScoutLogin />} />
            <Route path="/signup/player" element={<Signup userType="player" />} />
            <Route path="/signup/admin" element={<Signup userType="admin" />} />
            <Route path="/signup/scout" element={<ScoutSignup />} />
            <Route path="/login/bookie" element={<BookieLogin />} />
            <Route path="/signup/bookie" element={<BookieSignup />} />

            {/* Public Side Action Results (no auth required) */}
            <Route path="/live/side-action" element={<PublicSideActionList />} />
            <Route path="/live/side-action/:leagueId" element={<PublicSideActionView />} />

            {/* Dashboard Routes */}
            <Route path="/player/dashboard" element={
              <ProtectedRoute userType="player">
                <PlayerDashboard />
              </ProtectedRoute>
            } />
            <Route path="/subscription-success" element={
              <ProtectedRoute userType="player">
                <SubscriptionSuccess />
              </ProtectedRoute>
            } />
            <Route path="/admin/dashboard" element={
              <ProtectedRoute userType="admin">
                <AdminDashboardRouter />
              </ProtectedRoute>
            } />
            <Route path="/scout/dashboard" element={
              <ProtectedRoute userType="scout">
                <ScoutDashboard />
              </ProtectedRoute>
            } />
            <Route path="/scout/bowlers/:bowlerId" element={
              <ProtectedRoute userType="scout">
                <ScoutBowlerDetail />
              </ProtectedRoute>
            } />
            <Route path="/admin/leagues/create" element={
              <ProtectedRoute userType="admin">
                <CreateLeague />
              </ProtectedRoute>
            } />
            <Route path="/admin/leagues/manage" element={
              <ProtectedRoute userType="admin">
                <ManageLeagues />
              </ProtectedRoute>
            } />
            <Route path="/admin/leagues/:id/manage" element={
              <ProtectedRoute userType="admin">
                <ManagingLeague />
              </ProtectedRoute>
            } />
            <Route path="/admin/bowlers/manage" element={
              <ProtectedRoute userType="admin">
                <BowlersManagement />
              </ProtectedRoute>
            } />
            <Route path="/admin/analytics" element={
              <ProtectedRoute userType="admin">
                <Analytics />
              </ProtectedRoute>
            } />
            <Route path="/player/analytics" element={
              <ProtectedRoute userType="player">
                <PlayerAnalytics />
              </ProtectedRoute>
            } />
            <Route path="/player/leagues/:leagueId" element={<BowlerLeagueView />} />
            <Route path="/player/side-action/:leagueId" element={
              <ProtectedRoute userType="player">
                <BowlerSideActionView />
              </ProtectedRoute>
            } />
            <Route path="/player/calendar" element={
              <ProtectedRoute userType="player">
                <BowlerCalendar />
              </ProtectedRoute>
            } />
            <Route path="/admin/bowlers/:bowlerId" element={
              <ProtectedRoute userType="admin">
                <BowlerDetail />
              </ProtectedRoute>
            } />
            <Route path="/admin/checkin/player/:playerId/league/:leagueId" element={
              <ProtectedRoute userType="admin">
                <CheckoutComponent />
              </ProtectedRoute>
            } />
            <Route path="/player/leagues/browse" element={
              <ProtectedRoute userType="player">
                <BrowseLeagues />
              </ProtectedRoute>
            } />
            <Route path="/player/leagues/browse/:id" element={
              <ProtectedRoute userType="player">
                <LeagueDetail />
              </ProtectedRoute>
            } />
            <Route path="/player/teams" element={
              <ProtectedRoute userType="player">
                <MyTeams />
              </ProtectedRoute>
            } />
            <Route path="/admin/leagues/:id/manage-staff" element={
              <ProtectedRoute userType="admin">
                <ManageStaff />
              </ProtectedRoute>
            } />
            <Route path="/admin/billing/checkout" element={
              <ProtectedRoute userType="admin">
                <AdminBillingCheckout />
              </ProtectedRoute>
            } />
            <Route path="/admin/billing/success" element={
              <ProtectedRoute userType="admin">
                <AdminBillingSuccess />
              </ProtectedRoute>
            } />
            <Route path="/bookie/dashboard" element={
              <ProtectedRoute userType="bookie">
                <SideActionDashboard />
              </ProtectedRoute>
            } />
            <Route path="/bookie/subscription-success" element={
              <ProtectedRoute userType="bookie">
                <BookieSubscriptionSuccess />
              </ProtectedRoute>
            } />
            <Route path="/bookie/create-league" element={
              <ProtectedRoute userType="bookie">
                <CreateExpressLeague />
              </ProtectedRoute>
            } />
            <Route path="/bookie/create-bowler" element={
              <ProtectedRoute userType="bookie">
                <CreateBowlerAccount />
              </ProtectedRoute>
            } />
            <Route path="/bookie/manage-league" element={
              <ProtectedRoute userType="bookie">
                <BookieLeagueList />
              </ProtectedRoute>
            } />
            <Route path="/bookie/manage-league/:leagueId" element={
              <ProtectedRoute userType="bookie">
                <BookieLeagueManager />
              </ProtectedRoute>
            } />
            <Route path="/bookie/manage-league/:leagueId/bowler-groups" element={
              <ProtectedRoute userType="bookie">
                <CreateBowlerGroups />
              </ProtectedRoute>
            } />
            <Route path="/bookie/quick-entries/:leagueId/:squadId" element={
              <ProtectedRoute userType="bookie">
                <QuickEntriesPage />
              </ProtectedRoute>
            } />
            <Route path="/bookie/quick-bracket-entries/:leagueId/:squadId" element={
              <ProtectedRoute userType="bookie">
                <QuickBracketEntriesPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/quick-entries/:leagueId/:squadId" element={
              <ProtectedRoute userType="admin">
                <AdminQuickEntriesPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/quick-bracket-entries/:leagueId/:squadId" element={
              <ProtectedRoute userType="admin">
                <AdminQuickBracketEntriesPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/side-events/:sideEventId/doubles" element={<DoublesList />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            {/* Redirect any unknown routes to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
};

export default App;
